import React, {useCallback, useContext, useEffect, useMemo, useState} from "react";
import {Box, Paper, Stack, Tab, Tabs, Typography, useTheme} from "@mui/material";
import {useTranslation} from "react-i18next";
import {useSetRecoilState} from "recoil";
import SectionHeader from "../../../util/component/SectionHeader";
import {IRsTabs} from "./models";
import InfoTab from "./tabs-section/tabs/InfoTab/InfoTab";
import {useParams} from "react-router-dom";
import { getDocumentsRs, getRepairShopById } from "../search-repair-shop/rsSearch.actions";
import {
    additionalInfoState,
    associationInfoState,
    documentsRsState,
    eParkingState,
    financialInfoState,
    generalInfoState,
    representativeInfoState,
} from "../search-repair-shop/state";
import DocumentRepositoryRs from "./tabs-section/tabs/DocumentRepository/DocumentRepositoryRs";
import ContentBody from "../common/content-body/ContentBody";
import TimeframePanel from './tabs-section/tabs/TimeFrame/TimeframePanel';
import {setSnackbar} from "../../../util/state/globalAction";
import {snackbarTypes} from "../../../util/state/globalVariables";
import {GlobalContext} from "../../../util/state/GlobalContextProvider";
import CustomLoader from "../../../util/component/CustomLoader";
import { ObjectSubtitle } from "../common/subtitle/types";
import RenderedSubtitle from "../common/subtitle/Subtitle";

const RsDetail = () => {
    const theme = useTheme();
    const params = useParams();
    const {t} = useTranslation(['network', 'translation']);
    const {dispatchGlobalData} = useContext(GlobalContext);
    const [subtitle, setSubtitle] = useState<ObjectSubtitle[]>([]);

    const setGeneralInfo = useSetRecoilState(generalInfoState);
    const setAdditionalInfo = useSetRecoilState(additionalInfoState);
    const setEparking = useSetRecoilState(eParkingState);
    const setRepresentativeInfo = useSetRecoilState(representativeInfoState);
    const setFinancialInfo = useSetRecoilState(financialInfoState);
    const setAssociationInfo = useSetRecoilState(associationInfoState);
    const setDocumentRs = useSetRecoilState(documentsRsState);
    const [tab, setTab] = useState<IRsTabs>(IRsTabs.INFO);
    const [isLoading, setLoading] = useState<boolean>(false);

    const getDocuments = () => {
        getDocumentsRs({id: params.id})
                    .then((res: any) => {
                        setDocumentRs(res?.document);
                    })
                    .catch(() => {
                        dispatchGlobalData({
                            type: setSnackbar,
                            payload: {
                                snackbar: {
                                    message: t('error.tryLater'),
                                    type: snackbarTypes.error,
                                    show: true,
                                    autoClose: true,
                                }
                            }
                        });
                    }).finally(() => {setLoading(false);});
    }

    const getInfo = () => {
        getRepairShopById({id: params.id})
                .then((res:any) => {
                    setGeneralInfo(res?.generalInfo);
                    setAdditionalInfo(res?.additionalInfo);
                    setEparking(res?.eparkingInfo);
                    setFinancialInfo(res?.financialInfo);
                    setRepresentativeInfo(res?.representativeInfo);
                    setAssociationInfo(res?.associationInfo);

                    setSubtitle([
                        {title: 'rsDetail.generalInfo.form.label.repairShopNaming', value: res?.generalInfo.repairShopNaming},
                        {title: 'rsDetail.generalInfo.form.label.sapId', value: res?.generalInfo.bpCode},
                    ]
                    )
                })
                .catch(() => {
                    dispatchGlobalData({
                        type: setSnackbar,
                        payload: {
                            snackbar: {
                                message: t('error.tryLater'),
                                type: snackbarTypes.error,
                                show: true,
                                autoClose: true,
                            }
                        }
                    });
                }).finally(() => {setLoading(false);});
    }

    const handleChangeTab = useCallback((_event: React.SyntheticEvent, newValue: number) => {
        setTab(newValue);
    }, []);

    const apiList = {
        [IRsTabs.INFO]: () => getInfo(),
        [IRsTabs.DOCUMENT_REPOSITORY]: () => getDocuments(),
        [IRsTabs.TIMEFRAME_INFO]: () => {setLoading(false);},
    }

    useEffect(() => {
        setLoading(true);
        apiList[tab as 0 | 1 | 2]?.()
    },[tab]);

    return(
        <>
            <SectionHeader title={t('rsDetail.title')} subtitle={<RenderedSubtitle subtitle={subtitle} />} />
            <ContentBody>
                <Paper elevation={1} sx={{p: theme.spacing(2), mb: theme.spacing(2)}}>
                    <Box>
                        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                            <Tabs value={tab} onChange={handleChangeTab}>
                                <Tab label={t("rsDetail.tab.info")}/>
                                <Tab label={t("rsDetail.tab.documents")}/>
                                <Tab label={t("rsDetail.tab.timeframe")}/>
                            </Tabs>
                        </Box>
                        <Box py={3}>
                            {tab === IRsTabs.INFO && <InfoTab/> }
                            {tab === IRsTabs.DOCUMENT_REPOSITORY && <DocumentRepositoryRs/> }
                            {tab === IRsTabs.TIMEFRAME_INFO && <TimeframePanel /> }
                        </Box>
                    </Box>
                </Paper>
            </ContentBody>
            <CustomLoader loading={isLoading} />
        </>
    )
}

export default RsDetail;